import React, { useMemo } from "react";

import CustomSection from "../custom-general/custom-section";
import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import CustomUTMReportPDF from "../custom-utm/custom-utm-report";
import PipingAnalytics from "../custom-visual-inspection/report-items/pipingAnalytics";
import ParseHTML from "../../common/ParseHTML";
import PDFTablePage from "../../themed/pdf/pdfTablePage";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import OverviewSheet from "../custom-general/overview-sheet";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

import checklistClassNames from '../custom-visual-inspection/checklist/style.module.scss';

export interface RiserChecklistItem {
	component: string;
	assessmentResult: string;
	anomalyNumber: models.SelectOption[];
}

export interface UTMData {
	overview: models.CustomUTMOverview;
	inspectionItems: models.UTMInspection[];
	thicknessReadings: models.UTMReadingItem[];
}

export interface RiserChecklist { 
    checklist: RiserChecklistItem[], 
    comment: string; 
}

interface Props {
    reportTitle: string;
	anomalySummary: models.AnomalySummary;
    coverData: models.CustomFVIData;
    executiveSummary: string;
    projectDetails: models.Project;
    pidData: models.PIDData;
    isoImage: models.PDFImageProps[];
    anomalyItems: models.NewAnomalyItem[];
    photographicData: models.PDFImageProps[];
    checklistData: RiserChecklist;
    utmData: UTMData;
}

const CustomExxonMobilRiserReport: React.FC<Props> = ({ anomalySummary, checklistData, executiveSummary, isoImage, pidData, projectDetails, photographicData, coverData, anomalyItems, reportTitle, utmData }) => {
    const { inspectionTypeName } = projectDetails;

    const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

    const columns = useMemo(() => {
		return [
			{
				path: 'component',
				label: 'COMPONENT',
				headerStyle: { backgroundColor: '#ccccff' },
				style: () => ({ backgroundColor: '#dfe1e6', color: '#000000', textTransform: 'uppercase', textAlign: 'left' }),
			},
			{
				path: 'assessmentResult',
				label: 'ASSESSMENT RESULT',
				headerStyle: { backgroundColor: '#34cccd' },
			},
			{
				path: 'anomalyNumber',
				label: 'ANOMALY NUMBER',
				headerStyle: { backgroundColor: '#ccccff' },
				content: (item: RiserChecklistItem) => <span>{utils.formatNumberRange(item.anomalyNumber.map((photo) => photo.id))}</span>
			},
		]
	}, []);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<CustomSection sectionTitle="CheckList">
                <div className="p-3">
                    <PDFTablePage data={checklistData.checklist} columns={columns as models.Column[]} />

                    <div className="my-1"></div>

                    <div className={checklistClassNames.checklistTableComment}>
                        <div className={checklistClassNames.checklistTableCommentHeader}>
                            Additional comment, if any
                        </div>
                        <div className={checklistClassNames.checklistTableCommentBody}>
							<ParseHTML text={checklistData.comment} />
                        </div>
                    </div>
                </div>
            </CustomSection>

			<CustomUTMReportPDF
				tmlLocations={isoImage}
				utInspectionItems={utmData.inspectionItems}
				inspectionOverview={utmData.overview}
			/>

			<PipingAnalytics
				isChecklistVisible={false}
				items={[
					{
						hasPDO: "no",
						pdoChecklist: [],
						lineNumber: 'Riser Inspection',
						hasPID: pidData.hasPID,
						anomalySummary: anomalySummary, 
						hasAnomalies: anomalyItems.length > 0 ? 'yes' : 'no', 
						anomalyItems: anomalyItems,
						checklistItems: checklistData.checklist as any,
						checklistAdditionalComment: checklistData.comment,
						pIDImages: pidData.pIDImages,
						pIDNumber: pidData.pIDNumber,
						generalPhotos: photographicData,
						anomalyLocations: [],
						id: 1,
						inspectionDescription: 'No anomaly in visual inspection'
					}
				]}
			/>
        </React.Fragment>
	);
};
 
export default withPDF(CustomExxonMobilRiserReport);